import React, { useState } from 'react';
import { Brain, Target, Check, ChevronRight } from 'lucide-react';
import { regions, DIFFICULTY_LEVELS } from '../data/questionsData';

const ExamSetup = ({ onStartExam }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRegions, setSelectedRegions] = useState(['cabeza', 'cuello', 'torax', 'abdomen', 'espalda', 'pelvis', 'brazo', 'pierna']);
  const [selectedDifficulty, setSelectedDifficulty] = useState(DIFFICULTY_LEVELS.BASICO);

  const toggleRegion = (regionId) => {
    setSelectedRegions((prev) => {
      if (prev.includes(regionId)) {
        return prev.filter((r) => r !== regionId);
      }
      return [...prev, regionId];
    });
  };

  const handleStart = () => {
    if (selectedRegions.length === 0) {
      alert('Selecciona al menos una región para el examen');
      return;
    }
    if (!selectedDifficulty) {
      alert('Selecciona un nivel de dificultad para el examen');
      return;
    }
    onStartExam({
      regions: selectedRegions,
      difficulty: selectedDifficulty
    });
  };

  const difficultyOptions = [
    {
      id: DIFFICULTY_LEVELS.BASICO,
      label: 'Examen Básico',
      description: 'Preguntas de nivel fundamental sobre la(s) región(es) elegida(s)'
    },
    {
      id: DIFFICULTY_LEVELS.GENERAL,
      label: 'Examen Intermedio',
      description: 'Preguntas de complejidad moderada y correlación anatómica'
    },
    {
      id: DIFFICULTY_LEVELS.COMPLEJO,
      label: 'Examen Complejo',
      description: 'Casos clínicos y correlaciones avanzadas para nivel profesional'
    }
  ];

  const canProceed = () => {
    if (currentStep === 1) return selectedRegions.length > 0;
    if (currentStep === 2) return !!selectedDifficulty;
    return false;
  };

  const handleNext = () => {
    if (!canProceed()) return;
    if (currentStep < 2) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-4 px-3 sm:py-6 sm:px-4">
      <div className="max-w-4xl w-full">
        <div className="text-center mb-6 sm:mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <div className="p-3 bg-white/80 rounded-2xl shadow-lg">
              <Brain className="w-10 h-10 text-purple-500" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">Configura tu Examen</h1>
          </div>
          <p className="text-lg text-gray-600">Examen de hasta 50 preguntas con tiempo limitado según nivel.</p>

          <div className="mt-4">
            <div className="flex justify-center items-center mb-3 sm:mb-4 px-2">
              {[{ step: 1, label: 'Regiones' }, { step: 2, label: 'Nivel' }].map(({ step, label }) => (
                <div key={step} className="flex items-center mx-2">
                  <div
                    className={`flex items-center justify-center w-9 h-9 sm:w-12 sm:h-12 rounded-full font-bold border-3 transition-all shadow-lg ${
                      step === currentStep
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-purple-300 scale-110'
                        : step < currentStep
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white border-green-300'
                        : 'bg-white text-gray-400 border-gray-300 hover:border-purple-400'
                    }`}
                  >
                    {step < currentStep ? <Check className="w-4 h-4 sm:w-5 sm:h-5" /> : step}
                  </div>
                  <div className="ml-2 hidden sm:block">
                    <div
                      className={`font-semibold text-xs sm:text-sm ${
                        step === currentStep
                          ? 'text-purple-700'
                          : step < currentStep
                          ? 'text-green-700'
                          : 'text-gray-500'
                      }`}
                    >
                      {label}
                    </div>
                  </div>
                  {step < 2 && (
                    <div
                      className={`w-8 sm:w-16 h-0.5 mx-2 sm:mx-4 transition-all ${
                        step < currentStep
                          ? 'bg-gradient-to-r from-green-400 to-emerald-400'
                          : 'bg-gray-300'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="sm:hidden text-sm font-semibold text-purple-700">
              {currentStep === 1 ? 'Regiones' : 'Nivel del examen'}
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl p-4 sm:p-6 md:p-8 lg:p-10 border border-purple-100 space-y-8">
          {currentStep === 1 && (
          <div>
            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 flex items-center justify-center gap-2 sm:gap-3">
              <Target className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500" />
              Selecciona Regiones para el Examen
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 text-center mb-4 sm:mb-6">
              Puedes combinar varias regiones (por ejemplo, brazo y cabeza) y las preguntas se mezclarán en el examen.
            </p>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6 px-1 sm:px-0 justify-items-center">
              {regions.map((region) => {
                const isSelected = selectedRegions.includes(region.id);
                return (
                  <button
                    type="button"
                    key={region.id}
                    onClick={() => toggleRegion(region.id)}
                    className={`w-full max-w-[140px] sm:max-w-[180px] relative p-3 sm:p-6 rounded-xl sm:rounded-2xl border-3 transition-all transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                      isSelected
                        ? 'bg-gradient-to-br from-purple-100 to-pink-100 border-purple-400 text-purple-800 shadow-purple-200'
                        : 'bg-white border-gray-200 text-gray-600 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-2 sm:gap-3">
                      <span className="text-2xl sm:text-4xl">{region.icon}</span>
                      <span className="font-semibold text-center text-xs sm:text-sm">{region.name}</span>
                      {isSelected && (
                        <div className="absolute top-1 right-1 sm:top-2 sm:right-2 w-4 h-4 sm:w-6 sm:h-6 bg-purple-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-purple-50 px-3 sm:px-4 py-2 rounded-full border border-purple-200">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500" />
                <span className="text-xs sm:text-sm font-semibold text-purple-700">
                  {selectedRegions.length} {selectedRegions.length === 1 ? 'región seleccionada' : 'regiones seleccionadas'}
                </span>
              </div>
            </div>
          </div>
          )}

          {currentStep === 2 && (
          <div>
            <h2 className="text-lg sm:text-2xl md:text-3xl font-bold text-gray-800 mb-2 sm:mb-3 text-center">
              Elige el Nivel del Examen
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-600 text-center mb-4 sm:mb-6">
              Crea exámenes básicos, intermedios o complejos según tu nivel actual.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
              {difficultyOptions.map((option) => {
                const isSelected = selectedDifficulty === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setSelectedDifficulty(option.id)}
                    className={`w-full mx-auto p-4 sm:p-6 rounded-xl sm:rounded-3xl border-3 transition-all transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl ${
                      isSelected
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100 border-green-400 text-green-800 shadow-green-200'
                        : 'bg-white border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50'
                    }`}
                  >
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-2">
                        <Brain className="w-6 h-6 sm:w-8 sm:h-8" />
                      </div>
                      <h3 className="font-bold text-base sm:text-lg mb-1">{option.label}</h3>
                      <p className="text-xs sm:text-sm opacity-80">{option.description}</p>
                      {isSelected && (
                        <div className="mt-3 inline-flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-full">
                          <Check className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          <div className="pt-2 flex gap-3 sm:gap-4 justify-center">
            {currentStep > 1 && (
              <button
                type="button"
                onClick={handleBack}
                className="flex-1 max-w-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all"
              >
                ← Atrás
              </button>
            )}

            {currentStep < 2 ? (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canProceed()}
                className="flex-1 max-w-xs flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all"
              >
                Siguiente
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStart}
                disabled={!canProceed()}
                className="flex-1 max-w-xs flex items-center justify-center gap-2 sm:gap-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white px-4 sm:px-6 py-3 rounded-xl sm:rounded-2xl font-bold text-sm sm:text-base shadow-lg hover:shadow-xl transition-all"
              >
                Comenzar Examen
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamSetup;
